import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Search, ChevronLeft, ChevronRight, ChevronDown, Workflow, Layers, Database, PieChart, ArrowUpDown, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { StageBadge, stageDotClass, CountryFlag, resolveCountry } from '../components/process/processUi';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const StatTile = ({ label, value, sub, icon: Icon, tone, loading }) => (
  <Card className="border-gray-200 card-hover">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          {loading ? (
            <Skeleton className="h-8 w-20 mt-2" />
          ) : (
            <p className="text-3xl font-heading font-bold mt-1 tabular-nums">{value}</p>
          )}
          {!loading && sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`w-11 h-11 rounded-lg ${tone} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CountryChips = ({ countries }) => {
  if (!countries || countries.length === 0) {
    return <span className="text-xs text-muted-foreground">Any country</span>;
  }
  const shown = countries.slice(0, 2);
  const rest = countries.length - shown.length;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {shown.map(c => {
        const country = resolveCountry(c);
        return (
          <Badge key={c} variant="outline" className="gap-1.5 font-normal text-xs whitespace-nowrap pl-1.5" title={country.name}>
            <CountryFlag code={country.code} className="!w-4 !h-3" />{country.name}
          </Badge>
        );
      })}
      {rest > 0 && (
        <Badge variant="secondary" className="font-normal text-xs" title={countries.slice(2).map(c => resolveCountry(c).name).join(', ')}>+{rest}</Badge>
      )}
    </div>
  );
};

const ProfileCount = ({ count, clientCount }) => {
  if (!count) {
    return <span className="text-xs text-muted-foreground">No profiles</span>;
  }
  return (
    <div className="flex items-center justify-end gap-2">
      {clientCount > 0 && (
        <span className="text-xs text-amber-700" title="Client-specific profiles">{clientCount} client</span>
      )}
      <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-md bg-primary/10 text-primary font-semibold tabular-nums">
        {count}
      </span>
    </div>
  );
};

export default function ProcessesPage() {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stage, setStage] = useState(null);
  const [onlyWithProfiles, setOnlyWithProfiles] = useState(false);
  const [sort, setSort] = useState('grouped');
  const [collapsedStages, setCollapsedStages] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (stage) params.append('lifecycle_stage', stage);
      if (onlyWithProfiles) params.append('has_profiles', 'true');
      params.append('sort', sort);
      params.append('page', String(currentPage));
      params.append('page_size', String(pageSize));
      const { data } = await axios.get(`${API_URL}/processes?${params}`);
      setProcesses(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (error) {
      toast.error('Failed to load processes');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, stage, onlyWithProfiles, sort, currentPage, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, stage, onlyWithProfiles, sort, pageSize]);

  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  useEffect(() => {
    axios.get(`${API_URL}/processes/summary`)
      .then(res => setSummary(res.data))
      .catch(() => { /* tiles and pills just stay empty */ });
  }, []);

  const coverage = summary?.total_processes
    ? Math.round((summary.processes_with_profiles / summary.total_processes) * 100)
    : 0;
  const hasFilters = Boolean(debouncedSearch || stage || onlyWithProfiles);
  const grouped = sort === 'grouped';
  const NO_STAGE = '__none__';
  const stageMeta = useMemo(() => {
    const map = {};
    (summary?.lifecycle_stages || []).forEach(s => { map[s.stage] = s; });
    return map;
  }, [summary]);
  const toggleStage = (key) => setCollapsedStages(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const columnCount = grouped ? 6 : 7;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="processes-page">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Processes</h1>
          <p className="text-muted-foreground mt-1">
            Manufacturing processes and the activity profiles used to calculate their footprint
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile label="Processes" value={summary?.total_processes ?? '—'} icon={Workflow}
            tone="bg-primary/10 text-primary" loading={!summary}
            sub={summary ? `${summary.lifecycle_stages.length} lifecycle stages` : null} />
          <StatTile label="Profile coverage" value={`${coverage}%`} icon={PieChart}
            tone="bg-amber-100 text-amber-700" loading={!summary}
            sub={summary ? `${summary.processes_with_profiles} of ${summary.total_processes} have profiles` : null} />
          <StatTile label="Activity profiles" value={summary?.total_profiles ?? '—'} icon={Layers}
            tone="bg-sky-100 text-sky-700" loading={!summary}
            sub={summary?.processes_with_profiles ? `~${(summary.total_profiles / summary.processes_with_profiles).toFixed(1)} per covered process` : null} />
          <StatTile label="Process data rows" value={summary?.total_process_data ?? '—'} icon={Database}
            tone="bg-violet-100 text-violet-700" loading={!summary}
            sub="Inputs & outputs across profiles" />
        </div>

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ID, process name, type, sub type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 input-eco"
                  data-testid="process-search-input"
                />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Switch id="only-profiles" checked={onlyWithProfiles} onCheckedChange={setOnlyWithProfiles} data-testid="only-profiles-switch" />
                  <Label htmlFor="only-profiles" className="text-sm cursor-pointer whitespace-nowrap">Only with profiles</Label>
                </div>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-48 input-eco" data-testid="process-sort">
                    <ArrowUpDown className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grouped">Group by stage</SelectItem>
                    <SelectItem value="profiles">Sort: Most profiles</SelectItem>
                    <SelectItem value="name">Sort: Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {summary?.lifecycle_stages?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="stage-pills">
                <button
                  type="button"
                  onClick={() => setStage(null)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    !stage ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-200 hover:border-primary/50'
                  }`}
                >
                  All stages <span className="opacity-70 tabular-nums ml-1">{summary.total_processes}</span>
                </button>
                {summary.lifecycle_stages.map(({ stage: s, count }) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStage(stage === s ? null : s)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      stage === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${stage === s ? 'bg-white' : stageDotClass(s)}`} />
                    {s}
                    <span className="opacity-70 tabular-nums">{count}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <p className="text-sm text-muted-foreground">
                {loading ? 'Loading…' : `${total} process${total === 1 ? '' : 'es'}`}
                {stage && <> in <span className="font-medium text-foreground">{stage}</span></>}
              </p>
              {hasFilters && (
                <Button variant="ghost" size="sm" className="h-7 px-2"
                  onClick={() => { setSearch(''); setStage(null); setOnlyWithProfiles(false); }}>
                  <X className="w-3 h-3 mr-1" /> Clear filters
                </Button>
              )}
            </div>

            {loading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : total === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Workflow className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="font-medium">No processes found</p>
                <p className="text-sm text-muted-foreground mt-1">Try a different search or stage</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 table-dense">
                        <TableHead>Process</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Sub Type</TableHead>
                        {!grouped && <TableHead>Lifecycle Stage</TableHead>}
                        <TableHead>Countries</TableHead>
                        <TableHead className="text-right">Profiles</TableHead>
                        <TableHead className="w-8" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {processes.map((p, idx) => {
                        const stageKey = p.lifecycle_stage || NO_STAGE;
                        const prevKey = idx > 0 ? (processes[idx - 1].lifecycle_stage || NO_STAGE) : null;
                        const showHeader = grouped && stageKey !== prevKey;
                        const collapsed = grouped && collapsedStages.has(stageKey);
                        const meta = stageMeta[p.lifecycle_stage];
                        return (
                          <Fragment key={p.process_id}>
                            {showHeader && (
                              <TableRow
                                className="bg-gray-50/80 hover:bg-gray-100 cursor-pointer border-t-2 border-t-gray-200"
                                onClick={() => toggleStage(stageKey)}
                                data-testid={`stage-group-${stageKey}`}
                              >
                                <TableCell colSpan={columnCount} className="py-2.5">
                                  <div className="flex items-center gap-3">
                                    {collapsed
                                      ? <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                      : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                                    <span className={`w-2.5 h-2.5 rounded-full ${p.lifecycle_stage ? stageDotClass(p.lifecycle_stage) : 'bg-gray-300'}`} />
                                    <span className="font-heading font-semibold text-foreground">
                                      {p.lifecycle_stage || 'No lifecycle stage'}
                                    </span>
                                    {meta && (
                                      <span className="text-xs text-muted-foreground">
                                        {meta.count} process{meta.count === 1 ? '' : 'es'}
                                        {' · '}{meta.with_profiles} with profiles
                                      </span>
                                    )}
                                    {meta && (
                                      <span className={`ml-auto inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-0.5 ${
                                        meta.profile_count > 0 ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-muted-foreground'
                                      }`}>
                                        <Layers className="w-3 h-3" />
                                        {meta.profile_count} profile{meta.profile_count === 1 ? '' : 's'}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            {!collapsed && (
                              <TableRow
                                className="group cursor-pointer hover:bg-gray-50"
                                onClick={() => navigate(`/processes/${p.process_id}`)}
                                data-testid={`process-row-${p.process_id}`}
                              >
                                <TableCell className={grouped ? 'pl-10' : ''}>
                                  <div className="font-medium text-foreground">{p.process_name}</div>
                                  <div className="font-mono text-xs text-muted-foreground">#{p.process_id}</div>
                                </TableCell>
                                <TableCell>{p.process_type || <span className="text-muted-foreground">—</span>}</TableCell>
                                <TableCell>{p.process_sub_type || <span className="text-muted-foreground">—</span>}</TableCell>
                                {!grouped && <TableCell><StageBadge stage={p.lifecycle_stage} /></TableCell>}
                                <TableCell>
                                  {p.profile_count > 0 ? <CountryChips countries={p.countries} /> : <span className="text-muted-foreground">—</span>}
                                </TableCell>
                                <TableCell className="text-right">
                                  <ProfileCount count={p.profile_count} clientCount={p.client_profile_count} />
                                </TableCell>
                                <TableCell className="w-8">
                                  <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, total)} of {total}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Rows per page:</span>
                      <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
                        <SelectTrigger className="w-[70px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGE_SIZE_OPTIONS.map(size => (
                            <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                        <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                      </Button>
                      <span className="text-sm text-muted-foreground tabular-nums">{currentPage} / {totalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                        Next <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
